import { AlertTriangle, Ban, CircleCheckIcon, WrenchIcon } from "lucide-react";

import { UserAvatar } from "src/components/ui/avatar";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useAuth } from "src/lib/auth";
import { useAdminPrivacyMode } from "src/lib/hooks/useAdminPrivacyMode";
import { cn } from "src/lib/utils";

export const IntegrationCard = ({
  integration,
  description,
  type,
  onConnect,
  loading,
  isAdmin,
  onManage,
}) => {
  const { currentUser } = useAuth();
  const { privacyMode } = useAdminPrivacyMode();
  const connected = integration?.description;
  const isDisconnected = connected && integration?.isConnected === false;
  const hasActiveIssue = connected && !!integration?.activeIssue;
  const selectedSourcesCount = integration?.settings?.sources?.length || 0;
  const requiresSourceSelection = [
    "meta_ads",
    "google_ads",
    "tiktok_ads",
    "slack",
  ].includes(type?.id);
  const needsSetup =
    connected &&
    requiresSourceSelection &&
    selectedSourcesCount === 0 &&
    !isDisconnected;
  const connectedLabel = privacyMode
    ? process.env.APP_NAME
    : integration?.description || "Account";

  const canManage =
    isAdmin &&
    integration?.users?.some(
      ({ user, role }) => user.id === currentUser.id && role === "ADMIN"
    );
  const hasUsers = integration?.users?.length > 0;

  const managedBy = hasUsers ? (
    <div className="flex items-center">
      <span className="text-muted-foreground mr-4 text-sm">Managed by: </span>
      {integration?.users
        ?.filter(({ role, hidden }) => role === "ADMIN" && !hidden)
        ?.map(({ user }, index) => (
          <Tooltip key={user?.id || user?.userId || index}>
            <TooltipTrigger>
              <UserAvatar
                user={privacyMode ? null : user}
                className="-ml-2 size-5"
              />
            </TooltipTrigger>
            <TooltipContent>
              {privacyMode ? "Hidden admin" : user.name}
            </TooltipContent>
          </Tooltip>
        ))}
    </div>
  ) : null;

  return (
    <div
      className={cn(
        "flex w-full flex-col rounded-xl",
        connected
          ? "from-primary/80 via-border to-border bg-gradient-to-bl p-px"
          : "border-border border"
      )}
    >
      <div
        className={cn(
          "bg-card text-card-foreground flex size-full min-h-35 flex-col gap-4 rounded-[calc(var(--radius)+3px)] p-4"
        )}
      >
        <div className="flex w-full items-center space-x-2">
          {type.icon && (
            <type.icon
              className={cn("h-5", !connected && "text-muted-foreground")}
            />
          )}
          <h3
            className={cn(
              "leading-none font-semibold tracking-tight",
              !connected && "text-muted-foreground"
            )}
          >
            {type.name}
          </h3>

          {connected && (
            <div className="flex grow justify-end">
              {isDisconnected ? (
                <Badge variant="destructive">Paused</Badge>
              ) : hasActiveIssue ? (
                <Badge variant="destructive">Needs attention</Badge>
              ) : needsSetup ? (
                <Badge variant="outline">Needs setup</Badge>
              ) : (
                <CircleCheckIcon className="text-primary size-5" />
              )}
            </div>
          )}
        </div>

        <div className="flex h-full grow flex-col justify-between gap-1 text-sm">
          {connected ? (
            <>
              <div className="text-card-foreground">
                Connected to {connectedLabel}
              </div>

              <div className="text-muted-foreground">{description}</div>
              {isDisconnected && integration?.connectionStatusReason && (
                <div className="text-destructive text-xs">
                  {integration.connectionStatusReason}
                </div>
              )}
              {hasActiveIssue && integration?.healthStatusReason && (
                <div className="text-destructive text-xs">
                  <AlertTriangle className="mr-1 inline size-3 align-text-top" />
                  {integration.healthStatusReason}
                </div>
              )}
            </>
          ) : (
            <div className="text-muted-foreground">No accounts connected</div>
          )}
        </div>

        {managedBy}

        {!isAdmin ? (
          <span
            className={cn(
              "text-muted-foreground flex items-center gap-1 text-xs italic",
              hasUsers ? "-mt-2" : "mt-1"
            )}
          >
            <Ban className="size-3" />
            Must be an admin to manage this integration
          </span>
        ) : connected ? (
          canManage ? (
            <Button
              variant="secondary"
              className="mt-auto"
              disabled={loading || integration.syncing}
              onClick={onManage}
            >
              <WrenchIcon className="mr-2 size-4" />
              {integration.syncing
                ? "Syncing..."
                : hasActiveIssue
                  ? "Quick fix"
                  : "Manage"}
            </Button>
          ) : null
        ) : (
          <Button
            variant="outline"
            className={cn(
              "border-muted-foreground/70 bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground mt-auto border-dashed",
              loading && "opacity-50"
            )}
            onClick={onConnect}
            disabled={loading}
          >
            Connect
          </Button>
        )}
      </div>
    </div>
  );
};
